"use client";

import {
  Checkbox,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  LABEL,
} from "@/components/tool-kit/styles";
import type { ColumnExtractorStrings } from "@/components/tools/column-extractor";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type ColumnSpec,
  type Extraction,
  INPUT_FORMATS,
  type InputFormat,
  type ParsedInput,
  type SortDirection,
} from "@/lib/tool-kit/text/column-extractor";
import type { QuoteMode } from "@/lib/tool-kit/text/delimited";

/*
 * The column extractor's settings panels and result warnings. Split from the widget to
 * keep it readable; every word comes from the widget's strings.
 */

export const OUTPUT_DELIMITERS = {
  comma: ",",
  tab: "\t",
  semicolon: ";",
  pipe: "|",
  space: " ",
  custom: "",
} as const;

export type OutputDelimiter = keyof typeof OUTPUT_DELIMITERS;

const OUTPUT_DELIMITER_IDS = Object.keys(
  OUTPUT_DELIMITERS
) as OutputDelimiter[];
const QUOTE_MODES: readonly QuoteMode[] = ["needed", "always", "never"];
const SORT_DIRECTIONS: readonly SortDirection[] = ["asc", "desc"];
/** The input formats a detected delimiter is reported as. */
const NAMED_DELIMITERS: Readonly<Record<string, InputFormat>> = {
  ",": "comma",
  "\t": "tab",
  ";": "semicolon",
  "|": "pipe",
};
/** Rename boxes are offered for up to this many output columns. */
const MAX_RENAMES = 12;

function DetectedFormat({
  parsed,
  format,
  strings,
}: {
  parsed: ParsedInput;
  format: InputFormat;
  strings: ColumnExtractorStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (parsed.rows.length === 0) {
    return null;
  }
  const columns = Math.max(0, ...parsed.rows.map((row) => row.length));
  const known =
    parsed.delimiter === null ? format : NAMED_DELIMITERS[parsed.delimiter];
  const formatName = known
    ? strings.formats[known]
    : t(strings.detectedDelimiter, { delimiter: parsed.delimiter ?? "" });
  return (
    <p className={HINT}>
      {t(strings.detected, {
        format: formatName,
        rows: tPlural(strings.rowCount, locale, parsed.rows.length, {
          count: fmt(parsed.rows.length),
        }),
        columns: tPlural(strings.columnCountLabel, locale, columns, {
          count: fmt(columns),
        }),
      })}
    </p>
  );
}

export function InputSettings({
  format,
  onFormat,
  customDelimiter,
  onCustomDelimiter,
  startsText,
  onStartsText,
  onDetectStarts,
  hasHeader,
  onHasHeader,
  parsed,
  strings,
}: {
  format: InputFormat;
  onFormat: (format: InputFormat) => void;
  customDelimiter: string;
  onCustomDelimiter: (value: string) => void;
  startsText: string;
  onStartsText: (value: string) => void;
  onDetectStarts: () => void;
  hasHeader: boolean;
  onHasHeader: (value: boolean) => void;
  parsed: ParsedInput;
  strings: ColumnExtractorStrings;
}) {
  return (
    <div className="space-y-3">
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField
          id="colx-format"
          label={strings.formatLabel}
          onChange={onFormat}
          options={INPUT_FORMATS.map((value) => ({
            value,
            label: strings.formats[value],
          }))}
          value={format}
        />
        {format === "custom" ? (
          <TextField
            id="colx-custom-delimiter"
            label={strings.customDelimiterLabel}
            mono
            onChange={onCustomDelimiter}
            value={customDelimiter}
          />
        ) : null}
        {format === "fixed" ? (
          <div className="space-y-2">
            <TextField
              hint={strings.fixedStartsHint}
              id="colx-fixed-starts"
              label={strings.fixedStartsLabel}
              mono
              onChange={onStartsText}
              value={startsText}
            />
            <button
              className={BUTTON_SECONDARY}
              onClick={onDetectStarts}
              type="button"
            >
              {strings.detectStarts}
            </button>
          </div>
        ) : null}
      </div>
      <Checkbox
        checked={hasHeader}
        label={strings.hasHeader}
        onChange={onHasHeader}
      />
      <DetectedFormat format={format} parsed={parsed} strings={strings} />
    </div>
  );
}

export function ColumnPicker({
  count,
  headers,
  spec,
  onSpec,
  onAdd,
  resolved,
  outputColumns,
  renames,
  onRename,
  strings,
}: {
  count: number;
  headers: readonly string[] | null;
  spec: string;
  onSpec: (value: string) => void;
  onAdd: (column: number) => void;
  resolved: ColumnSpec;
  outputColumns: readonly string[];
  renames: readonly string[];
  onRename: (position: number, name: string) => void;
  strings: ColumnExtractorStrings;
}) {
  const fmt = useToolNumber();
  const columns = Array.from({ length: count }, (_, index) => index);
  // A rename box belongs to an output position, which the column list defines.
  const slots = resolved.columns.map((column, position) => ({
    column,
    position,
  }));
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className={LABEL}>{strings.columnsFound}</p>
        <div className="flex flex-wrap gap-2">
          {columns.map((column) => {
            const number = fmt(column + 1);
            const name =
              headers?.[column]?.trim() || t(strings.columnName, { number });
            return (
              <button
                aria-label={t(strings.addColumn, { number })}
                className={BUTTON_SMALL}
                key={column}
                onClick={() => onAdd(column + 1)}
                type="button"
              >
                {t(strings.columnChip, { number, name })}
              </button>
            );
          })}
        </div>
      </div>
      <TextField
        hint={strings.specHint}
        id="colx-spec"
        label={strings.specLabel}
        mono
        onChange={onSpec}
        placeholder={strings.specPlaceholder}
        value={spec}
      />
      {resolved.unknown.length > 0 ? (
        <p className="text-destructive text-sm" role="alert">
          {t(strings.unknownColumns, { list: resolved.unknown.join(", ") })}
        </p>
      ) : null}
      {spec.trim() && outputColumns.length <= MAX_RENAMES ? (
        <details className="rounded-lg border border-border bg-card p-3">
          <summary className={`${LABEL} cursor-pointer`}>
            {strings.renameHeading}
          </summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {slots.map(({ column, position }) => (
              <TextField
                id={`colx-rename-${position}`}
                key={position}
                label={t(strings.renameLabel, {
                  name:
                    headers?.[column]?.trim() ||
                    t(strings.columnName, { number: fmt(column + 1) }),
                })}
                onChange={(name) => onRename(position, name)}
                placeholder={outputColumns[position]}
                value={renames[position] ?? ""}
              />
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

export function RowSettings({
  trimCells,
  onTrimCells,
  skipEmptyRows,
  onSkipEmptyRows,
  dedupe,
  onDedupe,
  sortBy,
  onSortBy,
  sortDirection,
  onSortDirection,
  outputColumns,
  strings,
}: {
  trimCells: boolean;
  onTrimCells: (value: boolean) => void;
  skipEmptyRows: boolean;
  onSkipEmptyRows: (value: boolean) => void;
  dedupe: boolean;
  onDedupe: (value: boolean) => void;
  sortBy: string;
  onSortBy: (value: string) => void;
  sortDirection: SortDirection;
  onSortDirection: (value: SortDirection) => void;
  outputColumns: readonly string[];
  strings: ColumnExtractorStrings;
}) {
  const sortOptions = [
    { value: "none", label: strings.noSort },
    ...outputColumns.map((label, position) => ({
      value: String(position),
      label,
    })),
  ];
  const sortValue = Number(sortBy) < outputColumns.length ? sortBy : "none";
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Checkbox
          checked={trimCells}
          label={strings.trimCells}
          onChange={onTrimCells}
        />
        <Checkbox
          checked={skipEmptyRows}
          label={strings.skipEmptyRows}
          onChange={onSkipEmptyRows}
        />
        <Checkbox checked={dedupe} label={strings.dedupe} onChange={onDedupe} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField
          id="colx-sort"
          label={strings.sortLabel}
          onChange={onSortBy}
          options={sortOptions}
          value={sortValue}
        />
        {sortValue === "none" ? null : (
          <SelectField
            id="colx-order"
            label={strings.orderLabel}
            onChange={onSortDirection}
            options={SORT_DIRECTIONS.map((value) => ({
              value,
              label: strings.orders[value],
            }))}
            value={sortDirection}
          />
        )}
      </div>
    </div>
  );
}

export function OutputSettings({
  outputDelimiter,
  onOutputDelimiter,
  outputCustom,
  onOutputCustom,
  quoteMode,
  onQuoteMode,
  includeHeader,
  onIncludeHeader,
  strings,
}: {
  outputDelimiter: OutputDelimiter;
  onOutputDelimiter: (value: OutputDelimiter) => void;
  outputCustom: string;
  onOutputCustom: (value: string) => void;
  quoteMode: QuoteMode;
  onQuoteMode: (value: QuoteMode) => void;
  includeHeader: boolean;
  onIncludeHeader: (value: boolean) => void;
  strings: ColumnExtractorStrings;
}) {
  return (
    <div className="space-y-3">
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField
          id="colx-output-delimiter"
          label={strings.outputDelimiterLabel}
          onChange={onOutputDelimiter}
          options={OUTPUT_DELIMITER_IDS.map((value) => ({
            value,
            label: strings.outputDelimiters[value],
          }))}
          value={outputDelimiter}
        />
        {outputDelimiter === "custom" ? (
          <TextField
            id="colx-output-custom"
            label={strings.outputCustomLabel}
            mono
            onChange={onOutputCustom}
            value={outputCustom}
          />
        ) : null}
        <SelectField
          id="colx-quotes"
          label={strings.quoteLabel}
          onChange={onQuoteMode}
          options={QUOTE_MODES.map((value) => ({
            value,
            label: strings.quotes[value],
          }))}
          value={quoteMode}
        />
      </div>
      <Checkbox
        checked={includeHeader}
        label={strings.includeHeader}
        onChange={onIncludeHeader}
      />
    </div>
  );
}

/** What the parser repaired and the extraction dropped, one line each. */
export function Warnings({
  parsed,
  extraction,
  strings,
}: {
  parsed: ParsedInput;
  extraction: Extraction;
  strings: ColumnExtractorStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const counted = (template: string, count: number) =>
    count > 0 ? (
      <p className={HINT}>
        {tPlural(template, locale, count, { count: fmt(count) })}
      </p>
    ) : null;
  return (
    <>
      {parsed.unterminatedQuote ? (
        <Notice>{strings.unterminatedQuote}</Notice>
      ) : null}
      {counted(strings.strayQuotes, parsed.strayQuoteRows)}
      {counted(strings.shortRows, extraction.shortRows)}
      {counted(strings.duplicatesRemoved, extraction.duplicatesRemoved)}
      {counted(strings.emptyRowsSkipped, extraction.emptyRowsSkipped)}
    </>
  );
}
